import {InputText} from 'primereact/inputtext';
import {Button} from 'primereact/button';
import React from 'react';
import {Field, useForm} from '@tanstack/react-form';
import {useMutation} from '@tanstack/react-query';
import {fetch} from '../../shared/api/v1';
import type {components} from '../../shared/api/v1/openapi.types';
import {useAuth} from '../../providers/auth/auth-provider';
import {useMessage} from '../../providers/message/message-provider';
import {emailFormat, minLength, required} from '../../utils/validation';

type RequestPasswordResetDto = components['schemas']['RequestPasswordResetDto'];
type ResetPasswordDto = components['schemas']['ResetPasswordDto'];

type FormValues = ResetPasswordDto & {
  confirmPassword: string;
};

export const PasswordResetForm = () => {
  const {closePasswordResetModal, openSignInModal} = useAuth();
  const {showError, showMessage} = useMessage();
  const [cooldown, setCooldown] = React.useState(0);

  React.useEffect(() => {
    if (cooldown <= 0) {
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      setCooldown((prev) => Math.max(prev - 1, 0));
    }, 1000);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [cooldown]);

  const extractErrorMessage = (error: unknown) => {
    if (!error || typeof error !== 'object') {
      return undefined;
    }
    const maybeMessage = (error as { message?: string }).message;
    if (maybeMessage) {
      return maybeMessage;
    }
    const nestedMessage = (error as { error?: { message?: string } }).error?.message;
    if (nestedMessage) {
      return nestedMessage;
    }
    return (error as { data?: { message?: string } }).data?.message;
  };

  const requestMutation = useMutation<void, Error, RequestPasswordResetDto>({
    mutationFn: async (data: RequestPasswordResetDto) => {
      const response = await fetch.POST('/auth/password/reset/request', {
        body: data,
      });
      const error = (response as { error?: unknown }).error;
      if (error) {
        const message = extractErrorMessage(error);
        throw new Error(message ?? 'Не удалось отправить код');
      }
    },
    onSuccess: () => {
      showMessage({
        message: 'Код восстановления отправлен на email',
      });
    },
    onError: (error) => {
      showError({
        message: error.message || 'Не удалось отправить код',
      });
    },
  });

  const resetMutation = useMutation<void, Error, ResetPasswordDto>({
    mutationFn: async (data: ResetPasswordDto) => {
      const response = await fetch.POST('/auth/password/reset/confirm', {
        body: data,
      });
      const error = (response as { error?: unknown }).error;
      if (error) {
        const message = extractErrorMessage(error);
        throw new Error(message ?? 'Не удалось обновить пароль');
      }
    },
    onSuccess: () => {
      showMessage({
        message: 'Пароль успешно обновлён',
      });
      closePasswordResetModal();
      openSignInModal();
    },
    onError: (error) => {
      showError({
        message: error.message || 'Не удалось обновить пароль',
      });
    },
  });

  const form = useForm({
    defaultValues: {
      email: '',
      code: '',
      password: '',
      confirmPassword: '',
    } as FormValues,
    onSubmit: async ({value}) => {
      try {
        const payload: ResetPasswordDto = {
          email: value.email,
          code: value.code,
          password: value.password,
        };
        await resetMutation.mutateAsync(payload);
      } catch {
        // Errors are handled in onError
      }
    },
  });
  const Subscribe = form.Subscribe;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <div className="flex flex-column gap-2">
        <Field
          form={form}
          name="email"
          validators={{
            onChange: ({value}) => required(value) ?? emailFormat(value),
          }}
        >
          {(field) => (
            <div className="flex flex-column gap-2">
              <label htmlFor={field.name}>Email</label>
              <div className="flex gap-2">
                <InputText
                  id={field.name}
                  name={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  type="email"
                  className="flex-1"
                />
                <Subscribe<[string]>
                  selector={(state) => [state.values.email]}
                >
                  {([email]) => {
                    const emailError = required(email) ?? emailFormat(email);
                    const isDisabled = Boolean(emailError) || cooldown > 0 || requestMutation.isPending;
                    const label = cooldown > 0 ? `Запросить код (${cooldown})` : 'Запросить код';
                    return (
                      <Button
                        type="button"
                        disabled={isDisabled}
                        loading={requestMutation.isPending}
                        onClick={async () => {
                          if (emailError) {
                            return;
                          }
                          setCooldown(60);
                          await requestMutation.mutateAsync({email});
                        }}
                      >
                        {label}
                      </Button>
                    );
                  }}
                </Subscribe>
              </div>
              {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                <small className="p-error">{field.state.meta.errors[0]}</small>
              ) : null}
            </div>
          )}
        </Field>
        <Field
          form={form}
          name="code"
          validators={{
            onChange: ({value}) => required(value),
          }}
        >
          {(field) => (
            <div className="flex flex-column gap-2">
              <label htmlFor={field.name}>Код</label>
              <InputText
                id={field.name}
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
              />
              {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                <small className="p-error">{field.state.meta.errors[0]}</small>
              ) : null}
            </div>
          )}
        </Field>
        <Field
          form={form}
          name="password"
          validators={{
            onChange: ({value}) => required(value) ?? minLength(value, 6),
          }}
        >
          {(field) => (
            <div className="flex flex-column gap-2">
              <label htmlFor={field.name}>Новый пароль</label>
              <InputText
                id={field.name}
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                type="password"
              />
              {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                <small className="p-error">{field.state.meta.errors[0]}</small>
              ) : null}
            </div>
          )}
        </Field>
        <Field
          form={form}
          name="confirmPassword"
          validators={{
            onChange: ({value, fieldApi}) => {
              if (!value?.trim()) {
                return required(value);
              }
              const password = fieldApi.form.state.values.password;
              return value === password ? undefined : 'Пароли не совпадают';
            },
            onChangeListenTo: ['password'],
          }}
        >
          {(field) => (
            <div className="flex flex-column gap-2">
              <label htmlFor={field.name}>Повторите пароль</label>
              <InputText
                id={field.name}
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                type="password"
              />
              {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                <small className="p-error">{field.state.meta.errors[0]}</small>
              ) : null}
            </div>
          )}
        </Field>
        <div className="mt-2" />
        <Subscribe<[boolean, boolean]>
          selector={(state) => [state.canSubmit, state.isSubmitting]}
        >
          {([canSubmit, isSubmitting]) => (
            <div className="flex flex-column gap-2">
              <Button
                type="submit"
                disabled={!canSubmit}
                loading={isSubmitting || resetMutation.isPending}
              >
                Сохранить новый пароль
              </Button>
              <Button
                type="button"
                text
                onClick={() => {
                  closePasswordResetModal();
                  openSignInModal();
                }}
              >
                Вернуться ко входу
              </Button>
            </div>
          )}
        </Subscribe>
      </div>
    </form>
  );
}
